import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { IntelligenceEngineApi } from "@src/services/extractionApi";
import { sharePointApi } from "@src/services/sharepointApi";
import { useProcessNotifications } from "./useProcessNotifications";
import { useFileUpload } from "./useFileUpload";
import { useResultsManagement, type ReviewItemState } from "./useResultsManagement";
import { usePageNavigation } from "./usePageNavigation";
import { useToast, type ToastState } from "@hooks/useToast";
import { ProcessStatus } from "@models/index";
import type {
  IProcessInfoViewModel,
  IProcessItemViewModel,
  IExtractedNodeViewModel,
  IExtractedResultViewModel,
  IProcessedResultsFilterViewModel,
  SupportedDownloadFileType,
} from "@models/index";

export interface ExtractionOptions {
  pageNumbers?: string;
  instructions?: string;
  /** Per-run opt-in to mapping. Only offered when the doctype has an enabled
   *  mapping_config; defaults off. Drives whether the backend triggers mapping
   *  and whether the mapping status/redirect UI shows for this process. */
  mappingEnabled?: boolean;
}

export interface IExtractionState {
  startExtraction: (file: File, options?: ExtractionOptions) => Promise<void>;
  startSharePointImport: (folderPath: string, folderName: string, options?: ExtractionOptions, filePaths?: string[]) => Promise<boolean>;
  getExtractedResult: (
    processInfo: IProcessInfoViewModel,
    item?: IProcessItemViewModel,
  ) => Promise<void>;
  openProcessItem: (
    processId: string,
    itemId: string,
    doctypeCode: string,
  ) => Promise<ReviewItemState>;
  selectPage: (page: number) => void;
  selectNode: (node: IExtractedNodeViewModel) => void;
  getProcessedResultsInfo: (
    doctypeCode: string,
    filter?: IProcessedResultsFilterViewModel,
  ) => Promise<void>;
  setDoctypeCode: (code: string) => void;
  downloadResult: (
    processId: string,
    itemId: string,
    fileType?: SupportedDownloadFileType,
  ) => Promise<void>;
  downloadBatchResults: (processId: string) => Promise<void>;
  deleteProcess: (processId: string) => Promise<boolean>;
  deleteProcessItem: (processId: string, itemId: string) => Promise<boolean>;
  resetState: () => void;
  doctypeCode: string;
  selectedNode?: IExtractedNodeViewModel;
  extractedResult?: IExtractedResultViewModel;
  processResultsInfo: IProcessInfoViewModel[];
  selectedProcessResultInfo?: IProcessInfoViewModel;
  selectedProcessItem?: IProcessItemViewModel;
  totalPages: number;
  currentPage: number;
  isExtracting: boolean;
  isLoadingResult: boolean;
  isLoadingResults: boolean;
  uploadProgress: number;
  error: string | null;
  toast: ToastState;
  hideToast: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

export const useExtraction = (): IExtractionState => {
  // State management
  const [extractedResult, setExtractedResult] =
    useState<IExtractedResultViewModel>();
  const [processResultsInfo, setProcessResultsInfo] = useState<
    IProcessInfoViewModel[]
  >([]);
  const [selectedProcessResultInfo, setSelectedProcessResultInfo] =
    useState<IProcessInfoViewModel>();
  const [selectedProcessItem, setSelectedProcessItem] =
    useState<IProcessItemViewModel>();
  const [isExtracting, setIsExtracting] = useState(false);
  const [isLoadingResult, setIsLoadingResult] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [doctypeCode, setDoctypeCode] = useState<string>("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNode, setSelectedNode] = useState<IExtractedNodeViewModel>();
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setProcessResultsInfo([]);
    setExtractedResult(undefined);
    setSelectedNode(undefined);
    setSelectedProcessItem(undefined);
    setCurrentPage(1);
    setTotalPages(0);
    setError(null);
  }, []);

  const intelligenceEngineApi = useMemo(() => new IntelligenceEngineApi(), []);
  const { toast, showError, showSuccess, hideToast } = useToast();

  // Process IDs whose file upload is still in flight locally. The backend
  // creates the process record at presigned-URL time, so any list refetch
  // that lands during the upload would include it as PENDING and flash the
  // row before the progress bar finishes. We filter these IDs out of refetch
  // results until the local upload completes and inserts the row itself.
  const uploadingProcessIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    resetState();
  }, [doctypeCode, resetState]);

  const { connectToProcessNotifications, connectAndWaitForAuth } =
    useProcessNotifications(
      doctypeCode,
      intelligenceEngineApi,
      setProcessResultsInfo,
      showError,
    );

  const { startExtraction } = useFileUpload(
    intelligenceEngineApi,
    setIsExtracting,
    setError,
    setProcessResultsInfo,
    connectAndWaitForAuth,
    showError,
    showSuccess,
    setUploadProgress,
    doctypeCode,
    uploadingProcessIdsRef,
  );

  const startSharePointImport = useCallback(
    async (folderPath: string, folderName: string, options?: ExtractionOptions, filePaths?: string[]): Promise<boolean> => {
      if (!doctypeCode) {
        showError("Document type is required.");
        return false;
      }
      setIsExtracting(true);
      try {
        const response = await sharePointApi.importFolder(folderPath, {
          doctypeCode,
          pageNumbers: options?.pageNumbers,
          instructions: options?.instructions,
          mappingEnabled: options?.mappingEnabled,
        }, filePaths);
        const process: IProcessInfoViewModel = {
          id: response.process_id,
          status: ProcessStatus.Importing,
          createdAt: new Date().toISOString(),
          isBatch: true,
          errorMessage: null,
          items: [],
          mappingEnabled: options?.mappingEnabled ?? false,
        };
        setProcessResultsInfo((previous) => [process, ...previous]);
        void connectAndWaitForAuth(response.process_id).catch(() => {
          // Polling below still picks up status changes if the socket is unavailable.
        });
        showSuccess(`SharePoint import started: ${folderName}`);
        return true;
      } catch (error) {
        showError(error instanceof Error ? error.message : "SharePoint import failed");
        return false;
      } finally {
        setIsExtracting(false);
      }
    },
    [doctypeCode, connectAndWaitForAuth, showError, showSuccess],
  );

  // The import worker may take longer than a notification socket stays open.
  // Poll active batches through completion; socket updates can be missed
  // during long SharePoint imports or after a browser reconnect.
  useEffect(() => {
    const activeIds = processResultsInfo
      .filter((process) => process.isBatch && [
        ProcessStatus.Importing,
        ProcessStatus.BatchCreating,
        ProcessStatus.BatchCreated,
        ProcessStatus.Processing,
      ].some((status) => status === process.status))
      .map((process) => process.id);
    if (!activeIds.length) return;
    const timer = window.setInterval(() => {
      activeIds.forEach((id) => {
        void intelligenceEngineApi.getExtractionProcessesInfo(id)
          .then((updated) => setProcessResultsInfo((previous) =>
            previous.map((process) => process.id === id ? updated : process)))
          .catch(() => undefined);
      });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [intelligenceEngineApi, processResultsInfo]);

  const {
    getProcessedResultsInfo,
    getExtractedResult,
    openProcessItem,
    downloadResult,
    downloadBatchResults,
    deleteProcess,
    deleteProcessItem,
  } = useResultsManagement(
    intelligenceEngineApi,
    setIsLoadingResults,
    setIsLoadingResult,
    setError,
    setProcessResultsInfo,
    setExtractedResult,
    setSelectedProcessResultInfo,
    setSelectedProcessItem,
    setTotalPages,
    setCurrentPage,
    setSelectedNode,
    connectToProcessNotifications,
    showError,
    uploadingProcessIdsRef,
  );

  const { selectPage, selectNode } = usePageNavigation(
    extractedResult,
    totalPages,
    currentPage,
    setCurrentPage,
    setSelectedNode,
  );

  return {
    startExtraction,
    startSharePointImport,
    getExtractedResult,
    openProcessItem,
    selectPage,
    selectNode,
    getProcessedResultsInfo,
    setDoctypeCode,
    downloadResult,
    downloadBatchResults,
    deleteProcess,
    deleteProcessItem,
    resetState,
    doctypeCode,
    selectedNode,
    extractedResult,
    processResultsInfo,
    selectedProcessResultInfo,
    selectedProcessItem,
    totalPages,
    currentPage,
    isExtracting,
    isLoadingResult,
    isLoadingResults,
    uploadProgress,
    error,
    toast,
    hideToast,
    showSuccess,
    showError,
  };
};
